import * as THREE from 'three'
import { Text } from 'troika-three-text'

import MathsUtils from '@/utils/mathsUtils'

import BaseObject from './baseObject'
import EditingHandle from './editingHandle'

import EditorTypes from '../types'


// Editing Line
// An single segment line for tools to edit a line across the glass. Used for cutting, etc
export default class EditingLine extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)

    this.handleColour = '#FFFFFF'
    this.startPoint = new THREE.Vector2(0, 0)
    this.endPoint = new THREE.Vector2(500, 500)
    this.moveLastPoint = new THREE.Vector2(0, 0)
    this.startAngleFixed = false
    this.endAngleFixed = false
    this.useAngles = options.useAngles !== undefined ? !!options.useAngles : true

    this.flipStartAngle = false
    this.flipEndAngle = false

    this.textScalar = 0.9
    this.textScalarMax = 3.25  // Maximum size to scale the text. Stops text getting too big when zoomed right out
    
    this.outsideTextLabel = options.outsideTextLabel || ''
    this.insideTextLabel = options.insideTextLabel || ''

    this.showOutsideTextLabel = options.showOutsideTextLabel || false
    this.showInsideTextLabel = options.showInsideTextLabel || false
    this.swapDirectionLabels = false
    this.handlesEnabled = options.handlesEnabled !== undefined ? !!options.handlesEnabled : true
    this.showHandles = true

    this.mainLineObj = null
    this.mainLineGeom = null

    this.startHandle = null
    this.endHandle = null

    this.outsideTextObj = null
    this.insideTextObj = null

    // Linked Objects. Datum Lines / Segments if applicable
    this.linkedStartObj = null
    this.linkedEndObj = null
    
    this.listenMouseEvents = true

    this.sceneObject = new THREE.Object3D()
    this.updateSceneObject()

    this.startHandle = new EditingHandle(this.editor, { colour: this.handleColour })
    this.startHandle.setVisible(this.showHandles)
    this.addChildObject(this.startHandle)

    this.endHandle = new EditingHandle(this.editor, { colour: this.handleColour })
    this.endHandle.setVisible(this.showHandles)
    this.addChildObject(this.endHandle)

    this.startHandle.setPosition(this.startPoint)
    this.endHandle.setPosition(this.endPoint)
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData, intersectionInfo) {
    this.editor.captureEventObject(this)

    this.moveLastPoint.copy(this.startPoint)
    if(intersectionInfo) {
      if(intersectionInfo.intersection) {
        this.moveLastPoint.set(intersectionInfo.intersection.x, intersectionInfo.intersection.y, 0)
      }
    }
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    // If captured, move the editing handle around
    if(this.editor.isCapturedEventObject(this)) {
      let raycaster = mouseData.isSnapGridEnabled ? mouseData.snapRaycaster : mouseData.raycaster

      // Move the editing line around
      // Create a plane for the editing line
      // Assumes the editing shape is sitting on the plane at origin (Z = 0)
      var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
      var rayInt = new THREE.Vector3()
      if(raycaster.ray.intersectPlane(editingPlane, rayInt)) {

        // Cast out points from new position & let tool redo the raytrace
        let lineDir = this.endPoint.clone().sub(this.startPoint)
        lineDir.normalize()

        let newStartPoint = rayInt.clone().addScaledVector(lineDir, -10000)
        let newEndPoint = rayInt.clone().addScaledVector(lineDir, 10000)

        // Notify any listener the handle has been moved
        this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectMoved, {
          startPoint: newStartPoint,
          endPoint: newEndPoint,
          grabPoint: rayInt,
          moveOffset: rayInt.clone().sub(this.moveLastPoint)
        })

        this.moveLastPoint.set(rayInt.x, rayInt.y)

        this.editor.flagRenderRequired()
      }

    } else {
      if(this.editor.isSelectEnabled())
        this.editor.setMouseCursorHand()
    }
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionInfo) {
    // If captured, release it & notify listener
    if(this.editor.isCapturedEventObject(this)) {

      this.sendObjectEvent(EditorTypes.ObjectEventTypes.objectReleased)

      this.editor.releaseCapturedObject()
    }
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    let maxReadableAngle = 110
    var renderScale = 1.0 / this.editor.camera.zoom
    var labelScale = Math.min(renderScale * this.textScalar, this.textScalarMax)

    // Update positions/scales/rotations
    let angle = Math.atan2(this.endPoint.y - this.startPoint.y, this.endPoint.x - this.startPoint.x)
    let lineLength = this.endPoint.distanceTo(this.startPoint)
    let objAngles = new THREE.Euler(0, 0, angle)

    this.mainLineObj.position.set((this.endPoint.x + this.startPoint.x) / 2, (this.endPoint.y + this.startPoint.y) / 2, 90)
    this.mainLineObj.scale.set(lineLength, renderScale, 1)
    this.mainLineObj.setRotationFromEuler(objAngles)

    var labelScaleY = -labelScale   // Note: Flipped on Y axis as the text lib seems to be a different Y coord system to ours
    var textFlipped = false
    var textAngleDeg = MathsUtils.radiansToDegrees(angle)
    if(textAngleDeg >= 180)
      textAngleDeg -= 180

    // Flip text so it's not upside down
    let readableDist = MathsUtils.calculateShortestAngleDistanceDegrees(0, textAngleDeg)
    if(readableDist > maxReadableAngle) {
      textFlipped = true
      textAngleDeg = MathsUtils.wrapAngleDegrees(textAngleDeg - 180)
    }
    // Swap direction labels so inside/outside can match shape position (ie, inside side faces center of shape)
    if(this.swapDirectionLabels)
      textFlipped = !textFlipped

    // Update text labels
    if(this.outsideTextObj) {
      this.outsideTextObj.scale.x = labelScale
      this.outsideTextObj.scale.y = labelScaleY   
      this.outsideTextObj.scale.z = labelScale

      // Reposition label
      this.outsideTextObj.rotation.z = MathsUtils.degreesToRadians(textAngleDeg)
      this.outsideTextObj.position.set((this.endPoint.x + this.startPoint.x) / 2, (this.endPoint.y + this.startPoint.y) / 2, 99)
      this.outsideTextObj.anchorY = textFlipped ? 'top' : 'bottom'
    }
    if(this.insideTextObj) {
      this.insideTextObj.scale.x = labelScale
      this.insideTextObj.scale.y = labelScaleY
      this.insideTextObj.scale.z = labelScale

      // Reposition label
      this.insideTextObj.rotation.z = MathsUtils.degreesToRadians(textAngleDeg)
      this.insideTextObj.position.set((this.endPoint.x + this.startPoint.x) / 2, (this.endPoint.y + this.startPoint.y) / 2, 99)
      this.insideTextObj.anchorY = textFlipped ? 'bottom' : 'top'
    }

  }


  /**
   * Build the initial drawing objects to display the editing line
   */
  updateSceneObject() {
    this.clearSceneObject()
    let lineWidth = 2

    // Main line
    this.mainLineGeom = new THREE.BoxGeometry(1, lineWidth, 1)
    var material = new THREE.MeshBasicMaterial({
      color: this.editor.getTheme().editingLineColour
    })
    this.mainLineObj = new THREE.Mesh(this.mainLineGeom, material)
    this.sceneObject.add(this.mainLineObj)

    // Text Labels
    if(this.showOutsideTextLabel) {
      this.outsideTextObj = this._createTextLabel(this.outsideTextLabel)
      this.outsideTextObj.anchorY = 'bottom'
    }

    if(this.showInsideTextLabel) {
      this.insideTextObj = this._createTextLabel(this.insideTextLabel)
      this.insideTextObj.anchorY = 'top'
    }
  }

  setEnabled(enabled) {
    super.setEnabled(enabled)
    
    if(this.startHandle)
      this.startHandle.setEnabled(enabled && this.handlesEnabled)
    if(this.endHandle)
      this.endHandle.setEnabled(enabled && this.handlesEnabled)
  }


  // Set the start handle position
  setStartPoint(point) {
    this.startPoint.set(point.x, point.y)
    if(this.startHandle)
      this.startHandle.setPosition(point)
  }
  
  // Set the end handle position
  setEndPoint(point) {
    this.endPoint.set(point.x, point.y)
    if(this.endHandle)
      this.endHandle.setPosition(point)
  }

  // Get the line direction of the editing line
  getDirection() {
    let lineDir = new THREE.Vector2(this.endPoint.x - this.startPoint.x, this.endPoint.y - this.startPoint.y)
    lineDir.normalize()

    return lineDir
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    var handleRadiusTest = Math.max(worldRadius, 0.1)
    if(this.startHandle)
      handleRadiusTest += (this.startHandle.outerRadius * this.startHandle.handleObject.scale.x)
    let lineRadiusTest = Math.max(worldRadius, 0.1)

    // Create a plane for the editing handle
    // Assumes the editing shape is sitting on the plane at origin (Z = 0)
    var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(editingPlane, rayInt))
      return null

    let intPoint = MathsUtils.getClosest2DPointOnLineSegment(this.startPoint, this.endPoint, new THREE.Vector2(rayInt.x, rayInt.y))
    let intPoint3D = new THREE.Vector3(intPoint.x, intPoint.y, 0)
    let dist = rayInt.distanceTo(intPoint3D)
    if(dist <= lineRadiusTest) {

      // If close to the ends, ignore it so the handles get priority
      if(this.showHandles) {
        let startDist = this.startPoint.distanceTo(intPoint3D)
        let endDist = this.endPoint.distanceTo(intPoint3D)
        if(startDist <= handleRadiusTest || endDist <= handleRadiusTest)
          return null
      }

      return {
        intersection: intPoint,
        position: new THREE.Vector2(rayInt.x, rayInt.y),
        objectId: this.id,
        distance: dist
      }
    }

    return null
  }


  /**
   * Reset the linked objects
   */
  resetLinkedObjects() {
    this.linkedStartObj = null
    this.linkedEndObj = null
  }


  /**
   * Set the start linked object
   */
  setStartLinkedObject(startObj) {
    this.linkedStartObj = startObj
  }


  /**
   * Set the end linked object
   */
  setEndLinkedObject(endObj) {
    this.linkedEndObj = endObj
  }


  /**
   * Set show outside text label
   */
  setShowOutsideTextLabel(showLabel) {
    this.showOutsideTextLabel = showLabel
    this.updateSceneObject()
  }


  /**
   * Set show inside text label
   */
  setShowInsideTextLabel(showLabel) {
    this.showInsideTextLabel = showLabel
    this.updateSceneObject()
  }


  /**
   * Set use angles flag
   */
  setUseAngles(useAngles) {
    this.useAngles = useAngles
  }


  /**
   * Create a text label for the editing line for displaying side info
   * Label defaults to invisible
   */
  _createTextLabel(text) {
    var textLabelObj = new Text()

    textLabelObj.text = text
    textLabelObj.position.set(0, 0, 0)
    textLabelObj.color = this.editor.getTheme().editingLineTextColour
    textLabelObj.fontSize = 15
    textLabelObj.anchorX = 'center'
    textLabelObj.anchorY = 'bottom'

    this.sceneObject.add(textLabelObj)
    textLabelObj.addEventListener('synccomplete', () => {
      this.editor.flagRenderRequired()
    })
    textLabelObj.sync()

    return textLabelObj
  }
}
